import { useState } from 'react';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import SeoHead from '../../components/feature/SeoHead';

export default function Contact() {
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;
    setIsSubmitting(true);
    const form = e.target as HTMLFormElement;
    const formData = new FormData(form);
    const data = new URLSearchParams();
    for (const [key, value] of formData.entries()) {
      data.append(key, value.toString());
    }
    try {
      const response = await fetch('https://readdy.ai/api/form/d6p7msma739gopmlgmi0', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (response.ok) {
        setSubmitStatus('success');
        form.reset();
      } else {
        setSubmitStatus('error');
      }
    } catch {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-white">
      <SeoHead
        title="聯絡我們｜藍舍AI地產客服支援"
        description="有任何問題或建議？歡迎聯絡藍舍AI地產，AI智能房地產平台，出租、找房、廣告、管理一手包辦，我們將視情況回覆您的諮詢需求。"
        keywords="聯絡藍舍AI地產,客服支援,租房諮詢,藍舍AI地產聯絡"
        canonical="/contact"
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "ContactPage",
            "name": "聯絡我們 - 藍舍AI地產",
            "url": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/contact`,
            "description": "聯絡藍舍AI地產，AI智能房地產平台",
            "breadcrumb": {
              "@type": "BreadcrumbList",
              "itemListElement": [
                {"@type": "ListItem", "position": 1, "name": "首頁", "item": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/`},
                {"@type": "ListItem", "position": 2, "name": "聯絡我們", "item": `${import.meta.env.VITE_SITE_URL || 'https://ailanshe.com'}/contact`}
              ]
            }
          }
        ]}
      />
      <Navbar />

      {/* Beta 聲明橫幅 */}
      <div className="bg-amber-50 border-b border-amber-200 mt-14 md:mt-16">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-start gap-3">
          <i className="ri-information-line text-amber-500 text-lg flex-shrink-0 mt-0.5"></i>
          <p className="text-xs text-amber-800 leading-relaxed">
            <strong>測試版說明：</strong>本平台目前處於 Beta 測試階段，客服回覆時間不定，無法保證即時或固定時段的服務。如有緊急租屋安全問題，請直接撥打 165 反詐騙專線。
          </p>
        </div>
      </div>

      {/* Hero Section */}
      <div className="relative h-44 md:h-64 flex items-end overflow-hidden">
        <img
          src="https://public.readdy.ai/ai/img_res/cfccb2770a554501905868371b9879bb.jpg"
          alt="聯絡我們背景"
          className="hidden md:block absolute inset-0 w-full h-full object-cover object-top"
        />
        {/* 手機版純色背景 */}
        <div className="md:hidden absolute inset-0 bg-teal-700" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/15 via-black/30 to-black/65"></div>
        <div className="relative z-10 w-full pb-7 md:pb-10">
          <div className="max-w-6xl mx-auto px-4 md:px-6 text-center">
            <h1 className="text-3xl md:text-6xl font-black text-white mb-2 md:mb-3 drop-shadow-lg">聯絡我們</h1>
            <p className="text-sm md:text-xl text-white/90 drop-shadow">有任何問題或建議？歡迎傳訊息給我們</p>
          </div>
        </div>
      </div>

      <div className="pt-10 md:pt-16 pb-16 md:pb-20">
        <div className="max-w-6xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12">

            {/* 聯絡表單 */}
            <div className="bg-stone-50 rounded-3xl p-6 md:p-10">
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-5 md:mb-6">發送訊息</h2>

              {/* 成功/失敗回饋 */}
              {submitStatus === 'success' && (
                <div className="mb-5 flex items-start gap-3 bg-teal-50 border border-teal-200 rounded-xl px-4 py-4">
                  <i className="ri-checkbox-circle-fill text-teal-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-bold text-teal-800">訊息已送出</p>
                    <p className="text-xs text-teal-600 mt-0.5">我們已收到您的來信，將視情況於近期回覆。目前為測試階段，回覆時間不定，感謝您的耐心等待。</p>
                  </div>
                </div>
              )}
              {submitStatus === 'error' && (
                <div className="mb-5 flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl px-4 py-4">
                  <i className="ri-error-warning-fill text-red-500 text-xl flex-shrink-0 mt-0.5"></i>
                  <div>
                    <p className="text-sm font-bold text-red-700">送出失敗</p>
                    <p className="text-xs text-red-600 mt-0.5">請稍後再試，或直接發送 Email 至 info@ailanshe.com</p>
                  </div>
                </div>
              )}

              <form onSubmit={handleSubmit} id="contact-form" data-readdy-form>
                <div className="space-y-4 md:space-y-5">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">姓名 *</label>
                    <input
                      type="text" name="name" placeholder="請輸入您的姓名"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">電子郵件 *</label>
                    <input
                      type="email" name="email" placeholder="example@email.com"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">聯絡電話</label>
                    <input
                      type="tel" name="phone" placeholder="0912-345-678"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">主旨 *</label>
                    <select
                      name="subject"
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm cursor-pointer"
                      required
                    >
                      <option value="">請選擇</option>
                      <option value="一般諮詢">一般諮詢</option>
                      <option value="房源問題">房源問題</option>
                      <option value="技術支援">技術支援</option>
                      <option value="合作提案">合作提案</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">訊息內容 *</label>
                    <textarea
                      name="message" placeholder="請詳細描述您的問題或建議..."
                      rows={5} maxLength={500}
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent text-sm resize-none"
                      required
                    />
                    <p className="text-xs text-gray-500 mt-1">最多500字</p>
                  </div>
                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className={`w-full py-3.5 md:py-4 rounded-full font-bold text-base md:text-lg transition-colors shadow-lg whitespace-nowrap cursor-pointer flex items-center justify-center gap-2 ${isSubmitting ? 'bg-teal-300 text-white cursor-not-allowed' : 'bg-teal-500 text-white hover:bg-teal-600'}`}
                  >
                    {isSubmitting ? <><i className="ri-loader-4-line animate-spin"></i>送出中...</> : '送出訊息'}
                  </button>
                  <p className="text-xs text-gray-400 text-center">目前為測試期間，回覆時間不定，敬請見諒</p>
                </div>
              </form>
            </div>

            {/* 聯絡資訊 */}
            <div>
              <div className="mb-8 md:mb-10">
                <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-5 md:mb-6">聯絡資訊</h2>
                <div className="space-y-5 md:space-y-6">
                  {[
                    { icon: 'ri-mail-line', title: '電子郵件', lines: ['info@ailanshe.com'] },
                    {
                      icon: 'ri-time-line', title: '回覆說明',
                      lines: ['目前平台處於測試（Beta）階段', '回覆時間不定，無固定客服時段', '感謝您的耐心等待']
                    },
                  ].map((item) => (
                    <div key={item.title} className="flex items-start gap-3 md:gap-4">
                      <div className="w-10 h-10 md:w-12 md:h-12 bg-teal-100 rounded-full flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-lg md:text-xl text-teal-600`}></i>
                      </div>
                      <div>
                        <h3 className="font-bold text-gray-900 mb-1 text-sm md:text-base">{item.title}</h3>
                        {item.lines.map((line, i) => (
                          <p key={i} className={`text-sm ${i > 0 ? 'text-gray-400' : 'text-gray-600'}`}>{line}</p>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* 社群媒體 */}
              <div className="bg-gradient-to-br from-teal-500 to-teal-600 rounded-3xl p-6 md:p-8 text-white mb-6 md:mb-8">
                <h3 className="text-lg md:text-xl font-bold mb-3 md:mb-4">關注我們</h3>
                <p className="text-white/90 mb-4 md:mb-6 text-sm">在社群媒體上與我們互動，獲取最新資訊</p>
                <div className="grid grid-cols-2 gap-2 md:gap-3">
                  {[
                    { icon: 'ri-facebook-line', label: 'Facebook', href: '#' },
                    { icon: 'ri-instagram-line', label: 'Instagram', href: '#' },
                    { icon: 'ri-line-fill', label: 'LINE 官方帳號', href: 'https://line.me/R/ti/p/@381szuhf' },
                    { icon: 'ri-youtube-line', label: 'YouTube', href: '#' },
                  ].map((s) => (
                    <a
                      key={s.label}
                      href={s.href}
                      target={s.href !== '#' ? '_blank' : undefined}
                      rel="noopener noreferrer nofollow"
                      className="flex items-center gap-2 md:gap-3 bg-white/20 rounded-xl p-3 md:p-4 hover:bg-white/30 transition-colors cursor-pointer"
                    >
                      <i className={`${s.icon} text-xl md:text-2xl`}></i>
                      <span className="font-medium text-sm md:text-base">{s.label}</span>
                    </a>
                  ))}
                </div>
              </div>

              {/* 地圖 */}
              <div className="rounded-3xl overflow-hidden shadow-lg">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3615.0022789234567!2d121.56347!3d25.033964!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3442abb6da80a7ad%3A0xacc4d11dc963103c!2sTaipei%20101!5e0!3m2!1sen!2stw!4v1234567890"
                  width="100%"
                  height="240"
                  style={{ border: 0 }}
                  allowFullScreen
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="辦公室位置"
                ></iframe>
              </div>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
}
